// Store page (static demo pages/Store.jsx). Until floor plans exist (last
// release) every store is a store "without a layout" in the demo's sense:
// the Cabinets and Checks tabs, cabinets = the store's measuring points.
import { useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { clsName } from "../data/classes";
import { useT } from "../i18n";
import type { LocNode, St } from "../data/locations";
import { go } from "../lib/nav";
import { can } from "@gimasi/giothub-shell";
import { fleet } from "../data/fleet";
import { AddCabinetDialog } from "../components/AddCabinetDialog";

const ORDER: Record<St, number> = { crit: 0, alarm: 1, warn: 2, delayed: 3, ok: 5 };

export default function Store({ site }: { site: LocNode }) {
  const t = useT();
  const [tab, setTab] = useState<"cabinets" | "checks">("cabinets");
  const [adding, setAdding] = useState(false);
  const empty = site.empty || [];
  const rooms = site.points.filter((p) => p.kind === "room");
  const rows = [...site.points.filter((p) => p.kind !== "room")].sort((a, b) => ORDER[a.st] - ORDER[b.st] || a.name.localeCompare(b.name));
  const counts = rows.reduce<Record<string, number>>((m, r) => ((m[r.st] = (m[r.st] || 0) + 1), m), {});
  const account = fleet.value?.stores.find((x) => x.value === site.value)?.account_id || rows[0]?.owner || "";
  const top = rows.find((r) => r.st === "crit" || r.st === "alarm");
  return (
    <div class="page">
      <div class="page-h">
        <div>
          <h1>{site.name}</h1>
          <div class="sub">{counts.crit > 0 && <span class="st st-crit">{t("st.nCrit", { n: counts.crit })}</span>}{counts.alarm > 0 && <span class="st st-alarm">{t("st.nAlarm", { n: counts.alarm })}</span>}{counts.warn > 0 && <span class="st st-warn">{t("st.nWarn", { n: counts.warn })}</span>}{counts.delayed > 0 && <span class="st st-delayed">{t("st.nDelayed", { n: counts.delayed })}</span>}<span>{t("store.points", { n: rows.length })}</span></div>
        </div>
        <div class="row">{can("records:write") && <button class="btn" type="button" onClick={() => setAdding(true)}>{t("x.cab.add")}</button>}{top && <button class="btn pri" type="button" onClick={() => go("/alarms")}>{t("store.openAlarm", { cab: top.name })}</button>}<button class="btn" type="button">{t("store.haccp")}</button></div>
      </div>
      <div class="tabs" role="tablist">
        <button role="tab" aria-selected={tab === "cabinets"} class={tab === "cabinets" ? "on" : ""} onClick={() => setTab("cabinets")}>{t("store.tabCabs", { n: rows.length + empty.length })}</button>
        <button role="tab" aria-selected={tab === "checks"} class={tab === "checks" ? "on" : ""} onClick={() => setTab("checks")}>{t("store.tabChecks")}</button>
      </div>
      {tab === "cabinets" && (
        <>
          <div class="card row" style="justify-content:space-between"><span style="color:var(--muted)">{t("store.noLayout")}</span></div>
          <div class="cabs">{rows.map((o) => (
            <Link key={o.id} href={"/cabinets/" + o.id} class={"cab " + (o.st === "crit" ? "crit" : o.st === "warn" ? "warn" : o.st === "alarm" ? "alarm" : "")}>
              <div class="h"><b>{o.name}</b><span class={"st st-" + o.st}>{t("st." + o.st)}</span></div>
              <div class="temp tick" key={Math.round((o.temp || 0) * 10)}>{t.temp(o.temp)}</div>
              <small>{o.regime ? clsName(t.lang, o.regime) : ""}</small>
            </Link>))}
            {empty.map((c) => <div key={c.id} class="cab"><div class="h"><b>{c.name}</b><span class="st st-delayed">{t("st.none")}</span></div><div class="temp" style="color:var(--muted)">—</div><small>{c.regime ? clsName(t.lang, c.regime) : ""}</small></div>)}
          </div>
        </>
      )}
      {tab === "cabinets" && rooms.length > 0 && <section class="card">
        <div class="card-h"><h2>{t("x.room.title")}</h2></div>
        <div class="cabs">{rooms.map((o) => (
          <Link key={o.id} href={"/devices/" + o.id} class={"cab " + (o.st === "crit" ? "crit" : o.st === "warn" ? "warn" : o.st === "alarm" ? "alarm" : "")}>
            <div class="h"><b>{o.name}</b><span class={"st st-" + o.st}>{t("st." + o.st)}</span></div>
            <div class="temp">{t.temp(o.temp)}</div>
            <small>{o.humidity != null ? `${t.num(o.humidity)} % rH · ` : ""}{o.regime ? clsName(t.lang, o.regime) : ""}</small>
          </Link>))}</div>
      </section>}
      {adding && <AddCabinetDialog site={site.value} account={account} onClose={() => setAdding(false)} />}
      {tab === "checks" && <div class="card soon-card"><h2 style="margin:0">{t("store.tabChecks")}</h2><p style="margin:0;color:var(--muted)">{t("x.checksLater")}</p></div>}
    </div>
  );
}
